import { useCallback, useEffect, useRef, useState } from 'react';
import useLogger from '../../../hooks/useLogger';

type UseAudioRecorderReturn = {
  startSegment: () => Promise<void>;
  stopRecording: (leave: boolean) => void;
  isRecording: boolean | null;
  audioRecTermination: boolean;
};
type Options = {
  segmentDurationSec?: number;
  leave: boolean;
  onSegmentAvailable: (blob: Blob, startedAt: number) => void;
};
export const useAudioRecorder = ({
  onSegmentAvailable,
  leave,
  segmentDurationSec = 180
}: Options): UseAudioRecorderReturn => {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const isRecordingdRef = useRef(false);

  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const audioChunksRef = useRef<Float32Array[]>([]);
  const sampleRateRef = useRef<number>(44100);
  const segmentTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startedAtRef = useRef<number>(0); // 録音開始タイムスタンプ
  const isWorkletLoadedRef = useRef(false);
  const logger = useLogger();
  const [audioRecTermination, setAudioRecTermination] = useState(false);
  const startSegment = async () => {
    if (isRecordingdRef.current) {
      return;
    }
    try {
      isRecordingdRef.current = true;
      setIsRecording(true);

      startedAtRef.current = Date.now(); // 開始時刻セット

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      sampleRateRef.current = audioContext.sampleRate;
      if (!isWorkletLoadedRef.current) {
        await audioContext.audioWorklet.addModule('workletProcessor.js');
        isWorkletLoadedRef.current = true;
      }

      const sourceNode = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, 'recorder-processor');

      workletNode.port.onmessage = (event) => {
        const channelData = event.data as Float32Array;
        audioChunksRef.current.push(new Float32Array(channelData));
      };

      sourceNode.connect(workletNode);
      workletNode.connect(audioContext.destination);

      sourceNodeRef.current = sourceNode;
      workletNodeRef.current = workletNode;

      segmentTimerRef.current = setTimeout(() => {
        stop();
        setTimeout(startSegment, 0);
      }, segmentDurationSec * 1000);
    } catch (error) {
      logger.error(error);
    }
  };
  const stop = useCallback(async (): Promise<void> => {
    if (!isRecordingdRef.current) {
      return;
    }
    try {
      isRecordingdRef.current = false;
      setIsRecording(false);

      workletNodeRef.current?.disconnect();
      sourceNodeRef.current?.disconnect();
      if (audioContextRef.current?.state !== 'closed') {
        audioContextRef.current?.close();
      }
      isWorkletLoadedRef.current = false;

      const floatData = flattenArray(audioChunksRef.current);
      const wavBuffer = encodeWAV(floatData, sampleRateRef.current);
      const blob = new Blob([wavBuffer], { type: 'audio/wav' });

      onSegmentAvailable(blob, startedAtRef.current);

      // Reset
      audioChunksRef.current = [];
    } catch (error) {
      logger.error(error);
    }
  }, [isRecordingdRef, isRecording, workletNodeRef, sourceNodeRef, audioContextRef, onSegmentAvailable]);

  const stopRecording = useCallback(
    (leave: boolean) => {
      if (segmentTimerRef.current) {
        clearTimeout(segmentTimerRef.current);
      }
      stop();
      isRecordingdRef.current = false;
      setIsRecording(false);
      if (!leave) {
        setTimeout(startSegment, 0);
        return;
      }
      setAudioRecTermination(true);
    },
    [segmentTimerRef]
  );

  useEffect(() => {
    return () => {
      if (leave) {
        stopRecording(true);
      }
    };
  }, [stopRecording, leave]);

  return { startSegment, stopRecording, isRecording: isRecording, audioRecTermination };
};

function flattenArray(chunks: Float32Array[]): Float32Array {
  const length = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
  const result = new Float32Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

function encodeWAV(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  // RIFF identifier
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // Subchunk1Size
  view.setUint16(20, 1, true); // AudioFormat = PCM
  view.setUint16(22, 1, true); // NumChannels
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // ByteRate
  view.setUint16(32, 2, true); // BlockAlign
  view.setUint16(34, 16, true); // BitsPerSample
  writeString(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  // PCM samples
  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return buffer;
}
